import React, {
	createContext,
	PropsWithChildren,
	ReactElement,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';

import { switchHorizontal } from '@/assets/icons';
import { CopilotHeader } from '@/components/CopilotHeader';
import { PublicCopilotLayout } from '@/components/PublicCopilotLayout';
import { AsaphButton } from '@/components/shared/Button';
import { ItemsList } from '@/components/shared/ItemsList';
import { privacyPolicyLink, termsOfServiceLink } from '@/config/constants';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useAuth } from '@/providers/AuthProvider';
import { useEnvironment } from '@/providers/EnvironmentProvider';
import { GraphQlTransportWsNextOperation } from '@/providers/TeamActionSubscription';
import { SvgIcon } from '@material-ui/core';
import * as Sentry from '@sentry/react';
import axios, { AxiosError } from 'axios';
import { format } from 'date-fns';
import {
	OptionsObject,
	SnackbarKey,
	SnackbarMessage,
	useSnackbar,
} from 'notistack';

import { CopilotContext, CopilotState, sendMessageToPco } from '../CopilotPage';
import styles from '../CopilotPage.module.scss';

type AuthErrorResponse = {
	code: string;
	message: string;
};

const CopilotAuthenticateUser: React.FC = () => {
	const { authData, setToken, setState, token } = useContext(CopilotContext);
	const {
		api: { url },
		environment,
	} = useEnvironment();
	const { handleError } = useErrorHandler();

	useEffect(() => {
		const runAsync = async () => {
			try {
				const res = await axios.post(`${url}/copilot/auth`, authData);
				setToken(res.data.jwt);
				// Note that we setting the user here in the case of Copilot
				// because we haven't actually authenticated the user yet.
				// This happens in AuthProvider.authenticate and would render the app
				// in an authenticated state.
				Sentry.setUser({ id: res.data.userId });
				// If auth is successful, we send FETCH_SONGS to the Copilot extention.
				// The result of the Copilot extension script should send an event
				// with songs called ADD_SONGS, which will cause the
				// setState to change to CopilotState.ImportingSongs
				sendMessageToPco({ key: 'FETCH_SONGS' });
			} catch (err) {
				if (err instanceof AxiosError) {
					const code = (err.response?.data as AuthErrorResponse)
						?.code;
					console.log(code);
					if (code === 'UserNotFound') {
						setState(CopilotState.UserNotFound);
						return;
					}
					if (code === 'MembershipNotFound') {
						setState(CopilotState.Error);
						handleError({
							error: err,
							errorCode: 'MembershipNotFound',
							friendlyMessage:
								'Your user was found but has no memberships associated.',
						});
						return;
					}
				}
				setState(CopilotState.Error);
				handleError({
					error: err,
					errorCode: 'CopilotLoginError',
					friendlyMessage:
						'Something went wrong. Please try again later.',
				});
			}
		};
		if (authData && !token) {
			runAsync();
		}
	}, [authData, handleError, setState, setToken, url, token]);
	return (
		<>
			<h2>Progress</h2>
			<ItemsList
				items={[
					{
						label: 'Checking Asaph account...',
						checked: false,
					},
				]}
			/>
		</>
	);
};

export default CopilotAuthenticateUser;
